import type {
  LoginRequest,
  MeResponse,
  RegisterRequest,
} from '@/js/account/shared/model';
import {
  accountLogin,
  accountLogout,
  accountRegister,
} from '@/js/account/entities/auth/api/auth.api';
import { useAuthStore } from '@/js/account/entities/session';
import { refreshAccountCsrfToken } from '@/js/account/shared/api';
import { trackLogin, trackSignUp } from '@/js/lib/analytics';

export async function establishSession(
  credentialsAction: () => Promise<unknown>,
): Promise<MeResponse | null> {
  const store = useAuthStore();
  // ponytail: register/login must not race the initial bootstrap() GET /me probe.
  await store.bootstrap();
  store.invalidateSessionProbes();
  await credentialsAction();
  // Login/register regenerate the session — meta CSRF must be updated once.
  await refreshAccountCsrfToken();
  return store.fetchMe();
}

export async function signIn(
  payload: LoginRequest,
): Promise<MeResponse | null> {
  return establishSession(async () => {
    const result = await accountLogin(payload);
    trackLogin();

    return result;
  });
}

export async function signUp(
  payload: RegisterRequest,
): Promise<MeResponse | null> {
  // Tracked inside the callback: establishSession still has to refresh the CSRF token and fetch
  // /me afterwards, and a failure there must not swallow an account that was actually created.
  return establishSession(async () => {
    const result = await accountRegister(payload);
    trackSignUp(payload.sourceOfDiscovery ?? '');

    return result;
  });
}

export async function signOut(): Promise<void> {
  const store = useAuthStore();

  try {
    await accountLogout();
    await refreshAccountCsrfToken();
  }
  finally {
    store.clearUser();
  }
}
